import { useEffect, useState, useRef } from 'react';
import { supabase } from '@shared/lib/supabase';
import { useAuth } from '@dashboard/hooks/useAuth';
import ProfileTab from './ProfileTab';
import ApiKeysTab from './ApiKeysTab';
import StripeConnectTab from './StripeConnectTab';
import SquareConnectTab from './SquareConnectTab';
import SecurityTab from './SecurityTab';
import CheckoutCustomizeTab from './CheckoutCustomizeTab';
import type { Merchant, MerchantBranding, Profile } from '@shared/types';

type Tab = 'profile' | 'api-keys' | 'checkout' | 'stripe' | 'square' | 'security';

const TABS: { id: Tab; label: string; symbol: string }[] = [
  { id: 'profile', label: 'Profile', symbol: '\u25CB' },
  { id: 'api-keys', label: 'API Keys', symbol: '#' },
  { id: 'checkout', label: 'Checkout', symbol: '\u25CE' },
  { id: 'stripe', label: 'Stripe', symbol: '\u22A1' },
  { id: 'square', label: 'Square', symbol: '\u229E' },
  { id: 'security', label: 'Security', symbol: '\u2298' },
];

export default function SettingsPage() {
  const { user } = useAuth();
  const [profile, setProfile] = useState<Profile | null>(null);
  const [merchant, setMerchant] = useState<Merchant | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [saveError, setSaveError] = useState('');
  const [activeTab, setActiveTab] = useState<Tab>('profile');

  const [businessName, setBusinessName] = useState('');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [avatarUrl, setAvatarUrl] = useState('');
  const [tolerance, setTolerance] = useState('0.85');
  const [timezone, setTimezone] = useState(Intl.DateTimeFormat().resolvedOptions().timeZone);
  const [statementDescriptor, setStatementDescriptor] = useState('');
  const [branding, setBranding] = useState<MerchantBranding>({});
  const [defaultProcessor, setDefaultProcessor] = useState<'stripe' | 'square'>('stripe');
  const [squareNotice, setSquareNotice] = useState<{ type: 'success' | 'error'; message: string } | null>(null);

  const initialValues = useRef({ businessName: '', phone: '', tolerance: '0.85', timezone: '', statementDescriptor: '', avatarUrl: '', branding: {} as MerchantBranding });

  const hasUnsavedChanges =
    businessName !== initialValues.current.businessName ||
    phone !== initialValues.current.phone ||
    tolerance !== initialValues.current.tolerance ||
    timezone !== initialValues.current.timezone ||
    statementDescriptor !== initialValues.current.statementDescriptor ||
    avatarUrl !== initialValues.current.avatarUrl ||
    JSON.stringify(branding) !== JSON.stringify(initialValues.current.branding);

  const loadData = async () => {
    if (!user) return;
    const [profileRes, merchantRes] = await Promise.all([
      supabase.from('profiles').select('*').eq('id', user.id).maybeSingle(),
      supabase.from('merchants').select('*').eq('id', user.id).maybeSingle(),
    ]);

    const p = profileRes.data as unknown as Profile | null;
    const m = merchantRes.data as unknown as Merchant | null;

    if (p) setProfile(p);
    if (m) setMerchant(m);

    const bName = p?.business_name || m?.business_name || '';
    const pPhone = p?.phone || m?.phone || '';
    const pEmail = p?.email || m?.email || user.email || '';
    const pAvatar = p?.avatar_url || m?.avatar_url || '';
    const dTolerance = String(m?.settings?.default_tolerance || 0.85);
    const dTimezone = (m?.settings as Record<string, unknown>)?.timezone as string || Intl.DateTimeFormat().resolvedOptions().timeZone;
    const dStatement = ((m?.settings as Record<string, unknown>)?.statement_descriptor as string) || '';
    const dBranding = m?.branding || {};
    const dProcessor: 'stripe' | 'square' = m?.default_processor === 'square' ? 'square' : 'stripe';

    setBusinessName(bName);
    setPhone(pPhone);
    setEmail(pEmail);
    setAvatarUrl(pAvatar);
    setTolerance(dTolerance);
    setTimezone(dTimezone);
    setStatementDescriptor(dStatement);
    setBranding(dBranding);
    setDefaultProcessor(dProcessor);

    initialValues.current = {
      businessName: bName,
      phone: pPhone,
      tolerance: dTolerance,
      timezone: dTimezone,
      statementDescriptor: dStatement,
      avatarUrl: pAvatar,
      branding: dBranding,
    };

    setLoading(false);
  };

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('square') === 'connected') {
      setSquareNotice({ type: 'success', message: 'Square account connected successfully' });
      window.history.replaceState({}, '', window.location.pathname);
      loadData();
      setActiveTab('square');
    }
    const squareError = params.get('square_error');
    if (squareError) {
      const errorMessages: Record<string, string> = {
        missing_params: 'OAuth callback was incomplete. Please try again.',
        invalid_state: 'Security check failed. Please try again.',
        token_exchange_failed: 'Failed to exchange authorization code with Square. Please try again.',
        db_error: 'Unable to save Square credentials. Please try again.',
        access_denied: 'You cancelled the Square authorization.',
      };
      setSquareNotice({
        type: 'error',
        message: errorMessages[squareError] || `Square connection failed: ${squareError}`,
      });
      window.history.replaceState({}, '', window.location.pathname);
      setActiveTab('square');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleDefaultProcessorChange = async (value: 'stripe' | 'square') => {
    if (!user) return;
    setDefaultProcessor(value);
    const { error } = await supabase
      .from('merchants')
      .update({ default_processor: value })
      .eq('id', user.id);
    if (error) {
      setSaveError(error.message);
    } else if (merchant) {
      setMerchant({ ...merchant, default_processor: value } as Merchant);
    }
  };

  const handleSave = async () => {
    if (!user) return;
    setSaveError('');
    setSaveSuccess(false);
    setSaving(true);

    const { error: profileError } = await supabase
      .from('profiles')
      .update({
        business_name: businessName || null,
        phone: phone || null,
        avatar_url: avatarUrl || null,
        updated_at: new Date().toISOString(),
      })
      .eq('id', user.id);

    if (profileError) {
      setSaving(false);
      setSaveError(profileError.message);
      return;
    }

    if (merchant) {
      const updatedSettings = {
        ...merchant.settings,
        default_tolerance: parseFloat(tolerance),
        timezone,
        statement_descriptor: statementDescriptor || undefined,
      };

      const { error: merchantError, count } = await supabase
        .from('merchants')
        .update({
          business_name: businessName || null,
          phone: phone || null,
          avatar_url: avatarUrl || null,
          settings: updatedSettings,
          branding,
        })
        .eq('id', user.id)
        .select('id', { count: 'exact', head: true });

      if (merchantError) {
        setSaving(false);
        setSaveError(merchantError.message);
        return;
      }

      if (count === 0) {
        setSaving(false);
        setSaveError('Unable to save merchant settings. Your merchant account may not be fully set up.');
        return;
      }

      setMerchant({
        ...merchant,
        business_name: businessName || null,
        phone: phone || null,
        avatar_url: avatarUrl || null,
        settings: updatedSettings,
        branding,
      } as Merchant);
    }

    setProfile((prev) => prev ? {
      ...prev,
      business_name: businessName || null,
      phone: phone || null,
      avatar_url: avatarUrl || null,
    } : prev);

    initialValues.current = {
      businessName: businessName || '',
      phone: phone || '',
      tolerance,
      timezone,
      statementDescriptor,
      avatarUrl: avatarUrl || '',
      branding,
    };
    setSaving(false);
    setSaveSuccess(true);
    setTimeout(() => setSaveSuccess(false), 3000);
  };

  if (loading) {
    return (
      <div className="p-6">
        <h1 className="font-sans text-lg font-medium mb-5" style={{ color: 'var(--dash-text-primary)' }}>Settings</h1>
        <p className="font-sans text-sm" style={{ color: 'var(--dash-text-muted)' }}>Loading...</p>
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="p-6">
        <h1 className="font-sans text-lg font-medium mb-5" style={{ color: 'var(--dash-text-primary)' }}>Settings</h1>
        <p className="font-sans text-sm" style={{ color: 'var(--dash-text-muted)' }}>Profile not found. Please contact support.</p>
      </div>
    );
  }

  const hasMerchant = !!merchant;

  return (
    <div className="p-6 min-h-full">
      <div className="flex items-center justify-between mb-5">
        <h1 className="font-sans text-lg font-medium" style={{ color: 'var(--dash-text-primary)' }}>Settings</h1>
        {hasUnsavedChanges && (
          <span className="font-mono text-[10px] text-amber-400 uppercase tracking-widest px-2 py-1 rounded border border-amber-400/20 bg-amber-400/10">
            Unsaved changes
          </span>
        )}
      </div>

      <div className="flex gap-6">
        <div className="w-48 shrink-0">
          <nav className="rounded overflow-hidden" style={{ border: '1px solid var(--dash-border)', background: 'var(--dash-card)' }}>
            {TABS.map(({ id, label, symbol }) => (
              <button
                key={id}
                onClick={() => setActiveTab(id)}
                className="w-full flex items-center gap-2.5 px-4 py-2.5 text-[13px] font-sans transition-colors"
                style={{
                  borderBottom: '1px solid var(--dash-border-light)',
                  background: activeTab === id ? 'var(--dash-active-bg)' : undefined,
                  color: activeTab === id ? 'var(--dash-text-primary)' : 'var(--dash-text-muted)',
                }}
                onMouseEnter={(e) => { if (activeTab !== id) { e.currentTarget.style.background = 'var(--dash-hover-bg)'; e.currentTarget.style.color = 'var(--dash-text-secondary)'; } }}
                onMouseLeave={(e) => { if (activeTab !== id) { e.currentTarget.style.background = ''; e.currentTarget.style.color = 'var(--dash-text-muted)'; } }}
              >
                <span className="w-4 text-center font-mono text-sm">{symbol}</span>
                {label}
              </button>
            ))}
          </nav>
        </div>

        <div className="flex-1 min-w-0">
          {activeTab === 'profile' && (
            <ProfileTab
              userId={user!.id}
              businessName={businessName}
              setBusinessName={setBusinessName}
              phone={phone}
              setPhone={setPhone}
              email={email}
              avatarUrl={avatarUrl}
              setAvatarUrl={setAvatarUrl}
              timezone={timezone}
              setTimezone={setTimezone}
              saving={saving}
              saveSuccess={saveSuccess}
              saveError={saveError}
              onSave={handleSave}
            />
          )}
          {activeTab === 'api-keys' && hasMerchant && (
            <ApiKeysTab apiKey={merchant!.api_key} webhookSecret={merchant!.webhook_secret} />
          )}
          {activeTab === 'api-keys' && !hasMerchant && (
            <div className="rounded p-8 text-center" style={{ border: '1px solid var(--dash-border)', background: 'var(--dash-card)' }}>
              <p className="font-sans text-sm" style={{ color: 'var(--dash-text-muted)' }}>API keys will be available once your merchant account is fully set up.</p>
            </div>
          )}
          {activeTab === 'stripe' && (
            <StripeConnectTab
              merchant={merchant}
              statementDescriptor={statementDescriptor}
              setStatementDescriptor={setStatementDescriptor}
              saving={saving}
              saveSuccess={saveSuccess}
              saveError={saveError}
              onSave={handleSave}
            />
          )}
          {activeTab === 'checkout' && hasMerchant && (
            <CheckoutCustomizeTab
              userId={user!.id}
              branding={branding}
              setBranding={setBranding}
              merchantName={businessName || 'Your Business'}
              saving={saving}
              saveSuccess={saveSuccess}
              saveError={saveError}
              onSave={handleSave}
            />
          )}
          {activeTab === 'checkout' && !hasMerchant && (
            <div className="rounded p-8 text-center" style={{ border: '1px solid var(--dash-border)', background: 'var(--dash-card)' }}>
              <p className="font-sans text-sm" style={{ color: 'var(--dash-text-muted)' }}>Checkout customization will be available once your merchant account is fully set up.</p>
            </div>
          )}
          {activeTab === 'square' && hasMerchant && (
            <div className="space-y-5">
              {squareNotice && (
                <div
                  className="rounded px-4 py-3 flex items-center justify-between"
                  style={{
                    border: '1px solid',
                    borderColor: squareNotice.type === 'success' ? 'rgba(16,185,129,0.3)' : 'rgba(239,68,68,0.3)',
                    background: squareNotice.type === 'success' ? 'rgba(16,185,129,0.1)' : 'rgba(239,68,68,0.1)',
                  }}
                >
                  <p
                    className="font-sans text-sm"
                    style={{ color: squareNotice.type === 'success' ? '#6ee7b7' : '#fca5a5' }}
                  >
                    {squareNotice.message}
                  </p>
                  <button
                    onClick={() => setSquareNotice(null)}
                    className="font-mono text-xs transition-colors"
                    style={{ color: squareNotice.type === 'success' ? '#6ee7b7' : '#fca5a5' }}
                  >
                    Dismiss
                  </button>
                </div>
              )}
              <SquareConnectTab merchant={merchant!} onUpdate={loadData} />

              <div className="rounded p-5" style={{ border: '1px solid var(--dash-border)', background: 'var(--dash-card)' }}>
                <h2 className="font-mono text-[10px] uppercase tracking-widest mb-4" style={{ color: 'var(--dash-text-muted)' }}>Default Payment Processor</h2>
                <select
                  value={defaultProcessor}
                  onChange={(e) => handleDefaultProcessorChange(e.target.value as 'stripe' | 'square')}
                  className="w-full rounded px-3 py-2 font-sans text-sm focus:outline-none"
                  style={{
                    border: '1px solid var(--dash-border)',
                    background: 'var(--dash-input)',
                    color: 'var(--dash-select-text)',
                  }}
                >
                  <option value="stripe" disabled={!merchant!.stripe_onboarded}>
                    Stripe {!merchant!.stripe_onboarded ? '- not connected' : ''}
                  </option>
                  <option value="square" disabled={!merchant!.square_onboarded}>
                    Square {!merchant!.square_onboarded ? '- not connected' : ''}
                  </option>
                </select>
                <p className="font-sans text-[10px] mt-2" style={{ color: 'var(--dash-text-faint)' }}>
                  New orders will use this processor. You can connect both and switch anytime.
                </p>
              </div>
            </div>
          )}
          {activeTab === 'square' && !hasMerchant && (
            <div className="rounded p-8 text-center" style={{ border: '1px solid var(--dash-border)', background: 'var(--dash-card)' }}>
              <p className="font-sans text-sm" style={{ color: 'var(--dash-text-muted)' }}>Square will be available once your merchant account is fully set up.</p>
            </div>
          )}
          {activeTab === 'security' && <SecurityTab />}
        </div>
      </div>
    </div>
  );
}
